<template>
  <div class="mfa-step">
    <!-- Codes de secours après un enrôlement exigé -->
    <template v-if="recoveryCodes">
      <div class="input-title">
        <h2>{{ $t('mfa.recovery.title') }}</h2>
      </div>
      <RecoveryCodes :codes="recoveryCodes" @done="$emit('done', result)" />
    </template>

    <template v-else>
      <div class="input-title">
        <h2>{{ $t('mfa.login.title') }}</h2>
        <p v-if="isEnroll">{{ $t('mfa.login.enroll_subtitle') }}</p>
        <p v-else-if="method === 'recovery'">{{ $t('mfa.login.recovery_subtitle') }}</p>
        <p v-else-if="method === 'email'">{{ $t('mfa.login.email_subtitle', { email: emailSentTo || step.email }) }}</p>
        <p v-else>{{ $t('mfa.login.totp_subtitle') }}</p>
      </div>

      <div v-if="isEnroll" class="mfa-enroll">
        <img :src="step.qr" alt="QR code" class="mfa-qr" />
        <p class="mfa-secret-label">{{ $t('mfa.setup.manual_key') }}</p>
        <code class="mfa-secret">{{ step.secret }}</code>
      </div>

      <form @submit.prevent="submit">
        <input
          ref="code"
          v-model="code"
          type="text"
          :inputmode="method === 'recovery' ? 'text' : 'numeric'"
          autocomplete="one-time-code"
          class="mfa-code-input"
          :placeholder="method === 'recovery' ? 'XXXX-XXXX' : '123456'"
          :maxlength="method === 'recovery' ? 9 : 6"
          @input="onInput"
        />
        <p v-if="error" class="mfa-error">{{ error }}</p>
        <button type="submit" class="custom-btn primary" :disabled="loading || !code">
          {{ $t('mfa.login.submit') }}
        </button>
      </form>

      <div v-if="!isEnroll" class="mfa-alternatives">
        <a v-if="method !== 'totp'" class="underline" @click.prevent="switchMethod('totp')">{{ $t('mfa.login.use_app') }}</a>
        <a v-if="method !== 'recovery'" class="underline" @click.prevent="switchMethod('recovery')">{{ $t('mfa.login.use_recovery') }}</a>
        <a v-if="hasEmail && method !== 'email'" class="underline" @click.prevent="sendEmail">{{ $t('mfa.login.use_email') }}</a>
        <a v-if="method === 'email'" class="underline" @click.prevent="sendEmail">{{ $t('mfa.login.resend_email') }}</a>
      </div>

      <div class="mfa-alternatives">
        <a class="underline" @click.prevent="$emit('cancel')">{{ $t('mfa.login.back') }}</a>
      </div>
    </template>
  </div>
</template>

<script>
import authService from './services/auth/authService.js'
import RecoveryCodes from '../dash/settings/RecoveryCodes.vue'

// Étape 2 de la connexion : code de l'application, code de secours, code par email, ou enrôlement exigé par un admin
export default {
  name: 'MfaStep',
  components: { RecoveryCodes },
  props: {
    step: { type: Object, required: true }
  },
  data() {
    return {
      code: '',
      method: 'totp',
      loading: false,
      error: null,
      emailSentTo: null,
      recoveryCodes: null,
      result: null
    }
  },
  computed: {
    isEnroll() {
      return this.step.step === 'enroll'
    },
    hasEmail() {
      return (this.step.methods || []).includes('email')
    }
  },
  mounted() {
    this.focus()
  },
  methods: {
    focus() {
      this.$nextTick(() => this.$refs.code && this.$refs.code.focus())
    },
    errorText(res) {
      const key = res && res.data && res.data.error
      return key && this.$te(key) ? this.$t(key) : this.$t('mfa.error.generic')
    },
    // Soumission automatique dès 6 chiffres (application ou email)
    onInput() {
      this.error = null
      if (this.method !== 'recovery') {
        this.code = this.code.replace(/\D/g, '').slice(0, 6)
        if (this.code.length === 6 && !this.loading) this.submit()
      }
    },
    switchMethod(method) {
      this.method = method
      this.code = ''
      this.error = null
      this.focus()
    },
    async sendEmail() {
      this.error = null
      const res = await authService.mfaEmail({ challenge: this.step.challenge })
      if (!res.success || res.error) {
        this.error = this.errorText(res)
        return
      }
      this.emailSentTo = res.data.email
      this.switchMethod('email')
    },
    async submit() {
      if (this.loading || !this.code) return
      this.loading = true
      this.error = null
      const res = this.isEnroll
        ? await authService.mfaEnroll({ challenge: this.step.challenge, code: this.code })
        : await authService.mfaVerify({ challenge: this.step.challenge, code: this.code, method: this.method })
      this.loading = false
      if (!res.success || res.error) {
        this.error = this.errorText(res)
        this.code = ''
        // Challenge expiré ou trop d'essais : retour à l'étape mot de passe
        const key = res.data && res.data.error
        if (key === 'dash.error.mfa_challenge_expired' || key === 'dash.error.mfa_too_many_attempts') {
          this.$emit('cancel', this.error)
        }
        return
      }
      const { token, user, recovery_codes: codes } = res.data
      this.result = { token, user }
      if (codes && codes.length) {
        this.recoveryCodes = codes
        return
      }
      this.$emit('done', this.result)
    }
  }
}
</script>

<style lang="scss" scoped>
.mfa-step {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.mfa-enroll {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.mfa-qr {
  width: 180px;
  height: 180px;
}
.mfa-secret-label {
  margin: 0;
  font-size: 13px;
  color: #555e75;
}
.mfa-secret {
  font-size: 14px;
  letter-spacing: 1px;
  word-break: break-all;
  user-select: all;
}
form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.mfa-code-input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px;
  font-size: 20px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  text-align: center;
  letter-spacing: 6px;
  &:focus {
    outline: none;
    border-color: #6b1f4d;
  }
}
.mfa-error {
  margin: 0;
  color: #d32f2f;
  font-size: 14px;
}
.mfa-alternatives {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  a {
    cursor: pointer;
  }
}
</style>
